import React, { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X } from "lucide-react";

// Global promise that resolves with the BeforeInstallPromptEvent when
// Chrome/Edge/Android fires it. iOS Safari never fires this event.
let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new CustomEvent("rivendas:pwa-installable"));
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    try { localStorage.setItem("rivendas_pwa_installed", "1"); } catch { /* ignore */ }
  });
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator?.standalone === true;
}

function detectPlatform() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (isIOS) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

export default function InstallAppButton() {
  const [available, setAvailable] = useState(!!deferredPrompt);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("rivendas_pwa_dismissed") === "1"; } catch { return false; }
  });
  const [installed, setInstalled] = useState(() => {
    try { return localStorage.getItem("rivendas_pwa_installed") === "1"; } catch { return false; }
  });
  const [platform, setPlatform] = useState("unknown");
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    setPlatform(detectPlatform());
    const onAvail = () => setAvailable(true);
    window.addEventListener("rivendas:pwa-installable", onAvail);
    const onInstalled = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("rivendas:pwa-installable", onAvail);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (isStandalone() || installed || dismissed) return null;

  // Show for: (a) Chrome/Android with deferredPrompt ready, (b) iOS Safari
  // always (needs manual instruction), (c) desktop / Android without event
  // yet — friendly fallback tip.
  const showNative = available && deferredPrompt;
  const showIos = platform === "ios";
  const showFallback = !showNative && !showIos && (platform === "desktop" || platform === "android");
  if (!showNative && !showIos && !showFallback) return null;

  const install = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
          setInstalled(true);
          try { localStorage.setItem("rivendas_pwa_installed", "1"); } catch { /* ignore */ }
        } else {
          skip();
        }
        deferredPrompt = null;
        setAvailable(false);
      } catch {
        setAvailable(false);
      }
      return;
    }
    if (showIos) {
      setShowIosGuide(true);
    }
  };

  const skip = () => {
    setDismissed(true);
    try { localStorage.setItem("rivendas_pwa_dismissed", "1"); } catch { /* ignore */ }
  };

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-40 bg-card border border-primary/40 shadow-2xl shadow-primary/10 rounded-2xl p-4 flex items-center gap-3"
      >
        <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-sm">Instalar app Rivendas</p>
          <p className="text-xs text-muted-foreground">
            {showIos
              ? "1 toque: adiciona ícone e abre em tela cheia."
              : "Abre mais rápido e funciona offline."}
          </p>
        </div>
        <button
          data-testid="pwa-install-skip"
          onClick={skip}
          className="text-xs text-muted-foreground hover:text-foreground px-2"
          aria-label="Depois"
        >
          Depois
        </button>
        <button
          data-testid="pwa-install-btn"
          onClick={install}
          className="text-xs font-bold px-3 py-2 rounded-lg bg-primary text-primary-foreground"
        >
          {showIos ? "Como" : "Instalar"}
        </button>
      </div>

      {showIosGuide && (
        <div
          data-testid="pwa-ios-guide"
          className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
          onClick={() => setShowIosGuide(false)}
        >
          <div
            className="w-full max-w-md bg-card border border-border rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <p className="font-heading font-black text-lg">Instalar no iPhone</p>
              <button
                onClick={() => setShowIosGuide(false)}
                className="text-muted-foreground hover:text-foreground p-1"
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/15 text-primary font-heading font-black shrink-0">1</span>
                <div className="flex items-center gap-2 flex-1">
                  <p className="text-sm">Toque em <b>Compartilhar</b></p>
                  <Share size={20} className="text-primary" />
                  <p className="text-xs text-muted-foreground">(barra inferior do Safari)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/15 text-primary font-heading font-black shrink-0">2</span>
                <div className="flex items-center gap-2 flex-1">
                  <p className="text-sm">Role e toque em <b>Adicionar à Tela de Início</b></p>
                  <PlusSquare size={20} className="text-primary" />
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/15 text-primary font-heading font-black shrink-0">3</span>
                <p className="text-sm flex-1">Toque em <b>Adicionar</b> — o ícone do Rivendas aparece na tela inicial como um app normal.</p>
              </div>
              <button
                data-testid="pwa-ios-guide-done"
                onClick={() => { skip(); setShowIosGuide(false); }}
                className="w-full mt-2 text-sm font-bold px-4 py-3 rounded-lg bg-primary text-primary-foreground"
              >
                Já instalei — não mostrar mais
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
